import { useEffect, useMemo, useState } from "react"
import { usePersistedState } from "@/hooks/usePersistedState"
import { Loader2, Download } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  MonthYearPicker,
  lastCompleteMonth,
} from "@/components/ui/month-year-picker"
import { getAPCMBillingCodes, type APCMBillingCodesResponse } from "../api/apcm-ops.api"
import { formatDateOnly, parseApiTimestamp } from "@/lib/datetime"

// Date-only values (YYYY-MM-DD) must never go through `new Date(str)`: JS parses
// them as UTC midnight, so formatting in any US time zone shows the previous day.
// Timestamps (consent_date) go through Date so they render in local time.
function formatDate(value?: string | null): string {
  if (!value) return ""
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDateOnly(value, "MM/dd/yyyy", value)
  const d = parseApiTimestamp(value)
  if (!d) return value
  return d.toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" })
}

function monthLabel(monthYm: string): string {
  const [y, m] = monthYm.split("-").map((s) => parseInt(s, 10))
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { year: "numeric", month: "long" })
}

interface Props {
  orgId: string
}

export function BillingCodes({ orgId }: Props) {
  const [month, setMonth] = useState<string>(() => {
    const url = new URL(window.location.href)
    const fromUrl = url.searchParams.get("month")
    return fromUrl && /^\d{4}-\d{2}$/.test(fromUrl) ? fromUrl : lastCompleteMonth()
  })
  const [data, setData] = useState<APCMBillingCodesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = usePersistedState("view:billing-codes:search", "")

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get("month") !== month) {
      url.searchParams.set("month", month)
      window.history.replaceState({}, "", url.toString())
    }
  }, [month])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getAPCMBillingCodes(orgId, month)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || "Failed to load billing codes")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [orgId, month])

  const filteredEntries = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data.entries
    return data.entries.filter((e) => {
      const name = `${e.first_name} ${e.last_name}`.toLowerCase()
      return (
        name.includes(q) ||
        (e.mrn || "").toLowerCase().includes(q) ||
        (e.g_code || "").toLowerCase().includes(q) ||
        (e.pcp_name || "").toLowerCase().includes(q) ||
        e.icd_codes.some((c) => c.toLowerCase().includes(q))
      )
    })
  }, [data, search])

  const handleDownloadCSV = () => {
    if (!data) return
    const header = [
      "MRN",
      "First name",
      "Last name",
      "Date of birth",
      "Date of consent",
      "Date of service",
      "G code",
      "ICDs",
      "Billed",
      "PCP",
      "Notes",
    ]
    const rows = data.entries.map((e) => [
      e.mrn || "",
      e.first_name,
      e.last_name,
      formatDate(e.date_of_birth),
      formatDate(e.consent_date),
      formatDate(e.date_of_service),
      e.g_code,
      e.icd_codes.join("; "),
      "", // Billed — operator fills in spreadsheet
      e.pcp_name || "",
      "", // Notes — operator fills in spreadsheet
    ])
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n")
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `apcm-billing-codes-${month}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end md:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-0">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Month of service
            </label>
            <MonthYearPicker value={month} onChange={setMonth} />
          </div>
          <div className="min-w-0">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Search
            </label>
            <Input
              placeholder="Name, MRN, code, PCP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-[260px]"
            />
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadCSV}
          disabled={!data || data.entries.length === 0}
          className="gap-2 self-start md:self-auto"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </Button>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {data.total_classifications} classification{data.total_classifications === 1 ? "" : "s"} for {monthLabel(month)}
          </span>
          {Object.entries(data.g_code_counts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([code, count]) => (
              <Badge key={code} variant="secondary" className="font-mono">
                {code}: {count}
              </Badge>
            ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="py-12 text-center text-sm text-destructive">{error}</div>
      ) : filteredEntries.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          No billing codes for {monthLabel(month)}
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead>MRN</TableHead>
                <TableHead className="min-w-[140px]">Patient</TableHead>
                <TableHead>DOB</TableHead>
                <TableHead>Consent</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>G-Code</TableHead>
                <TableHead>ICD Codes</TableHead>
                <TableHead>PCP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.map((e) => (
                <TableRow key={e.patient_id}>
                  <TableCell className="font-mono text-xs">{e.mrn || "—"}</TableCell>
                  <TableCell className="font-medium whitespace-nowrap">
                    {e.first_name} {e.last_name}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {formatDate(e.date_of_birth) || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {formatDate(e.consent_date) || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {formatDate(e.date_of_service) || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className="font-mono">{e.g_code}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {e.icd_codes.length === 0 ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : (
                        e.icd_codes.map((c) => (
                          <Badge key={c} variant="outline" className="font-mono text-xs">
                            {c}
                          </Badge>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{e.pcp_name || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
